// El dashboard de una plataforma: lo mismo que el resumen para esa tienda, más lo que
// sólo esa plataforma sabe — ADR-2026-10-06-metricas-de-tienda-online,
// ADR-2026-10-06-metricas-de-tienda-en-el-backend.
//
// Tienda Nube: carritos abandonados, clientes nuevos vs. recurrentes, cupones, envíos, stock y margen.
// Mercado Libre: ingreso neto (después de la comisión), visitas y conversión, reputación,
// preguntas y reclamos, y qué publicaciones se miran y no se venden.
// Sólo lo que las APIs informan. Lo que la tienda no deja leer o no contestó se dice en su
// panel, con el motivo y cómo arreglarlo, en vez de un cero.
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  MessageCircleQuestion,
  Medal,
  PackageCheck,
  PackageOpen,
  ShieldAlert,
  ShoppingCart,
} from 'lucide-react';
import IconoDeCanal from '../IconoDeCanal';
import { useUserGroup } from '../../../hooks/useUserGroup';
import { boton } from '../botones';
import { SelectorDePeriodo, useMetricasVisibles } from './comunes';
import { useMetricasDeTienda } from './metricasApi';
import { AvisoDeFalta } from './ResumenDeMetricas';
import { diasDelPeriodo, numero, pesos, pesosCortos, porcentaje } from './formato';
import { BarrasHorizontales, GraficoDeLineas, Panel, Reparto, TarjetaKpi } from './graficos';
import {
  COLOR_DE_PLATAFORMA,
  NOMBRE_DE_PLATAFORMA,
  faltaDe,
  ingresoNeto,
  type MetricaId,
  type MetricasDePlataforma,
  type Periodo,
  type Plataforma,
} from './tipos';

/** Sólo para empresas dev_tester: el resto vuelve a Tienda online (ADR-2026-10-07-metricas-solo-para-dev-testers). */
export default function MetricasDePlataformaPage() {
  const visibles = useMetricasVisibles();
  const { isDevTester } = useUserGroup();
  if (isDevTester === false) return <Navigate to="/lanzamientos" replace />;
  // Todavía no se sabe (primer login sin caché): nada, ni datos ni pedido.
  if (!visibles) return null;
  return <DetalleDeMetricas />;
}

function DetalleDeMetricas() {
  const { plataforma: param } = useParams<{ plataforma: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const plataforma: Plataforma | null = param === 'tiendanube' || param === 'mercadolibre' ? param : null;
  // El período viaja desde el resumen: entrar al detalle no lo vuelve a 30.
  const [periodo, setPeriodo] = useState<Periodo>(
    ((location.state as { periodo?: Periodo } | null)?.periodo as Periodo) ?? 30,
  );
  const { datos, cargando, error, recargar } = useMetricasDeTienda(periodo, plataforma !== null);

  if (!plataforma) {
    return (
      <div className="mx-auto max-w-lg py-24 px-6 text-center">
        <p className="text-sm text-brand-black">Esa plataforma no existe.</p>
        <Link to="/lanzamientos" className="mt-4 inline-block text-sm text-brand-primary">
          Volver a Tienda online
        </Link>
      </div>
    );
  }

  const estado = datos?.[plataforma];
  const d = estado?.estado === 'ok' ? estado.metricas : null;

  let cuerpo: React.ReactNode;
  if (!datos) {
    cuerpo = error ? (
      <div className="mt-6">
        <AvisoDeFalta faltante={{ motivo: 'error', detalle: 'No pudimos traer las métricas.' }} onReintentar={recargar} />
      </div>
    ) : (
      <EsqueletoDelDetalle />
    );
  } else if (d) {
    cuerpo = <DetalleConDatos d={d} periodo={datos.dias} onReintentar={recargar} />;
  } else if (estado?.estado === 'reconectar') {
    cuerpo = (
      <div className="mt-6">
        <AvisoDeFalta faltante={{ motivo: 'sin_permiso', detalle: estado.motivo }} />
      </div>
    );
  } else if (estado?.estado === 'error') {
    cuerpo = (
      <div className="mt-6">
        <AvisoDeFalta faltante={{ motivo: 'error', detalle: estado.detalle }} onReintentar={recargar} />
      </div>
    );
  } else {
    // Sin conexión (o la plataforma no está habilitada para la empresa).
    cuerpo = (
      <div className="mt-6 rounded-3xl border border-dashed border-neutral-300 p-8 text-center">
        <p className="text-sm text-brand-black">
          {estado?.estado === 'no_disponible'
            ? `${NOMBRE_DE_PLATAFORMA[plataforma]} todavía no está disponible para tu cuenta.`
            : `Todavía no conectaste ${NOMBRE_DE_PLATAFORMA[plataforma]}.`}
        </p>
        {estado?.estado !== 'no_disponible' && (
          <button type="button" onClick={() => navigate('/mi-marca')} className={boton('primario', { extra: 'mt-4' })}>
            Conectar en Mi marca
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl py-6 px-4 md:py-10 md:px-8">
      {/* ⚠ El `<h1>` es hijo directo de esta fila (ver la nota de LanzamientoDetalle). */}
      <div className="flex flex-wrap items-center gap-y-3">
        <button
          type="button"
          onClick={() => navigate('/lanzamientos', { state: { periodo } })}
          aria-label="Volver a Tienda online"
          title="Volver a Tienda online"
          className="-ml-1 mr-2 grid h-7 w-7 shrink-0 place-items-center rounded-lg text-brand-black hover:bg-neutral-100"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <span className="mr-3 shrink-0">
          <IconoDeCanal canal={plataforma} />
        </span>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold text-brand-black md:text-2xl">
          {NOMBRE_DE_PLATAFORMA[plataforma]}
        </h1>
        <div className="ml-4 shrink-0">
          <SelectorDePeriodo valor={periodo} onCambiar={setPeriodo} />
        </div>
      </div>
      <p className="mt-1 pl-9 text-sm text-neutral-500">
        {d ? `${d.cuenta} · ` : ''}últimos {datos?.dias ?? periodo} días{cargando && datos ? ' · actualizando…' : ''}
      </p>
      {d?.truncado && (
        <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-900">
          Tuviste muchas órdenes en el período: los números son de las más recientes. Elegí un período más corto para
          verlo completo.
        </p>
      )}
      {cuerpo}
    </div>
  );
}

function EsqueletoDelDetalle() {
  return (
    <div aria-busy="true" aria-label="Cargando las métricas">
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[124px] animate-pulse rounded-2xl bg-neutral-100" />
        ))}
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <div className="h-72 animate-pulse rounded-2xl bg-neutral-100 lg:col-span-2" />
        <div className="h-72 animate-pulse rounded-2xl bg-neutral-100" />
      </div>
    </div>
  );
}

/** El contenido de un panel, o el aviso de por qué falta esa métrica. */
function O({
  d,
  metrica,
  onReintentar,
  children,
}: {
  d: MetricasDePlataforma;
  metrica: MetricaId;
  onReintentar?: () => void;
  children: React.ReactNode;
}) {
  const falta = faltaDe(d, metrica);
  return falta ? <AvisoDeFalta faltante={falta} onReintentar={onReintentar} compacto /> : <>{children}</>;
}

function SinDatos({ texto }: { texto: string }) {
  return <p className="text-sm text-neutral-500">{texto}</p>;
}

function DetalleConDatos({ d, periodo, onReintentar }: { d: MetricasDePlataforma; periodo: Periodo; onReintentar: () => void }) {
  const color = COLOR_DE_PLATAFORMA[d.plataforma];
  const contra = `vs. ${periodo} días anteriores`;
  const a = d.actual;
  const b = d.anterior;
  const fechas = d.serie?.map((p) => p.fecha) ?? diasDelPeriodo(periodo);

  return (
    <>
      {a && b ? (
        d.plataforma === 'tiendanube' ? (
          <KpisDeTiendaNube d={d} contra={contra} color={color} />
        ) : (
          <KpisDeMercadoLibre d={d} contra={contra} color={color} />
        )
      ) : (
        <div className="mt-6">
          <O d={d} metrica="ventas" onReintentar={onReintentar}>
            {null}
          </O>
        </div>
      )}

      {a && d.serie && (
        <div className="mt-3 grid gap-3 lg:grid-cols-3">
          <Panel titulo="Ventas por día" subtitulo="En pesos" className="lg:col-span-2">
            <GraficoDeLineas
              fechas={fechas}
              formatear={pesos}
              formatearEje={pesosCortos}
              series={[{ id: d.plataforma, nombre: 'Ventas', color, valores: d.serie.map((p) => p.ventas) }]}
            />
          </Panel>
          <Panel titulo="Lo más vendido" subtitulo={`Últimos ${periodo} días`}>
            {d.topProductos && d.topProductos.length > 0 ? (
              <BarrasHorizontales
                formatear={pesosCortos}
                filas={d.topProductos.map((p) => ({
                  id: p.nombre,
                  nombre: p.nombre,
                  valor: p.ventas,
                  detalle: `${numero(p.unidades)} u.`,
                  color,
                }))}
              />
            ) : (
              <SinDatos texto="No hubo ventas en el período." />
            )}
          </Panel>
        </div>
      )}

      {/* Sin órdenes, todo lo de Tienda Nube depende del mismo permiso: el aviso de arriba alcanza. */}
      {d.plataforma === 'tiendanube' ? (
        a && <DetalleDeTiendaNube d={d} onReintentar={onReintentar} />
      ) : (
        <DetalleDeMercadoLibre d={d} periodo={periodo} fechas={fechas} onReintentar={onReintentar} />
      )}
    </>
  );
}

function KpisDeTiendaNube({ d, contra, color }: { d: MetricasDePlataforma; contra: string; color: string }) {
  const a = d.actual!;
  const b = d.anterior!;
  const ticket = a.ordenes ? a.ventas / a.ordenes : 0;
  const ticketAntes = b.ordenes ? b.ventas / b.ordenes : 0;
  return (
    <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <TarjetaKpi etiqueta="Ventas" valor={pesosCortos(a.ventas)} actual={a.ventas} anterior={b.ventas} contra={contra} serie={d.serie?.map((p) => p.ventas)} colorSerie={color} ayuda="Órdenes pagas, sin canceladas" />
      <TarjetaKpi etiqueta="Órdenes" valor={numero(a.ordenes)} actual={a.ordenes} anterior={b.ordenes} contra={contra} />
      <TarjetaKpi etiqueta="Ticket promedio" valor={pesosCortos(ticket)} actual={ticket} anterior={ticketAntes} contra={contra} />
      <TarjetaKpi etiqueta="Unidades vendidas" valor={numero(a.unidades)} actual={a.unidades} anterior={b.unidades} contra={contra} />
    </div>
  );
}

// ── Mercado Libre ──────────────────────────────────────────────────────────

function KpisDeMercadoLibre({ d, contra, color }: { d: MetricasDePlataforma; contra: string; color: string }) {
  const a = d.actual!;
  const b = d.anterior!;
  // Los dos períodos salen de sus propias órdenes (la comisión de cada una).
  const neto = ingresoNeto(d, 'actual') ?? 0;
  const netoAntes = ingresoNeto(d, 'anterior') ?? 0;
  const ticket = a.ordenes ? a.ventas / a.ordenes : 0;
  const ticketAntes = b.ordenes ? b.ventas / b.ordenes : 0;
  const v = d.visitas;
  return (
    <>
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <TarjetaKpi etiqueta="Ventas" valor={pesosCortos(a.ventas)} actual={a.ventas} anterior={b.ventas} contra={contra} serie={d.serie?.map((p) => p.ventas)} colorSerie={color} ayuda="Órdenes pagas, sin canceladas" />
        <TarjetaKpi
          etiqueta="Ingreso neto"
          valor={pesosCortos(neto)}
          actual={neto}
          anterior={netoAntes}
          contra={contra}
          ayuda="Ventas menos la comisión de Mercado Libre. El envío que pagás todavía no se descuenta."
        />
        {v ? (
          <>
            <TarjetaKpi etiqueta="Visitas" valor={numero(v.actual)} actual={v.actual} anterior={v.anterior} contra={contra} serie={v.serie} colorSerie={color} />
            <TarjetaKpi
              etiqueta="Conversión"
              valor={porcentaje(a.ordenes / Math.max(1, v.actual))}
              actual={a.ordenes / Math.max(1, v.actual)}
              anterior={b.ordenes / Math.max(1, v.anterior)}
              contra={contra}
              ayuda="Ventas cada 100 visitas"
            />
          </>
        ) : (
          <div className="col-span-2">
            <O d={d} metrica="visitas">
              {null}
            </O>
          </div>
        )}
      </div>
      {/* Lo mismo que Tienda Nube informa de sus ventas, para comparar parejo. Un carrito con
          varios productos cuenta como una venta (`pack_id`). */}
      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-3">
        <TarjetaKpi etiqueta="Ventas (cantidad)" valor={numero(a.ordenes)} actual={a.ordenes} anterior={b.ordenes} contra={contra} />
        <TarjetaKpi etiqueta="Ticket promedio" valor={pesosCortos(ticket)} actual={ticket} anterior={ticketAntes} contra={contra} />
        <TarjetaKpi etiqueta="Unidades vendidas" valor={numero(a.unidades)} actual={a.unidades} anterior={b.unidades} contra={contra} />
      </div>
    </>
  );
}

const NIVELES = [
  { color: '#E5484D', nombre: 'Roja' },
  { color: '#F76B15', nombre: 'Naranja' },
  { color: '#F5C400', nombre: 'Amarilla' },
  { color: '#8BD14E', nombre: 'Verde claro' },
  { color: '#2BA84A', nombre: 'Verde' },
];

function tasa(t: number | null): string {
  return t === null ? '—' : porcentaje(t);
}

function DetalleDeMercadoLibre({
  d,
  periodo,
  fechas,
  onReintentar,
}: {
  d: MetricasDePlataforma;
  periodo: Periodo;
  fechas: string[];
  onReintentar: () => void;
}) {
  const r = d.reputacion;
  const comisiones = d.neto?.actual.comisiones ?? 0;
  const neto = ingresoNeto(d, 'actual');
  const envios = faltaDe(d, 'envios');
  const publicaciones = (d.topProductos ?? []).filter((p) => p.visitas !== undefined);
  return (
    <>
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        {/* Sin ventas leídas, el aviso de arriba ya lo dice: no se repite en cada panel que sale de ellas. */}
        {d.actual && (
        <Panel titulo="De la venta a lo que te queda" subtitulo={`Últimos ${periodo} días`}>
          <O d={d} metrica="neto" onReintentar={onReintentar}>
            {neto !== null && d.actual && d.actual.ventas > 0 ? (
              <>
                <Reparto
                  formatear={pesosCortos}
                  partes={[
                    { id: 'neto', nombre: 'Te queda', valor: neto, color: COLOR_DE_PLATAFORMA.mercadolibre },
                    { id: 'comision', nombre: 'Comisión de Mercado Libre', valor: comisiones, color: '#8A93A6' },
                  ]}
                />
                <p className="mt-4 border-t border-neutral-100 pt-3 text-xs text-neutral-500">
                  Sobre {pesos(d.actual.ventas)} vendidos.
                  {envios ? ` ${envios.detalle}` : ''}
                </p>
              </>
            ) : (
              <SinDatos texto="No hubo ventas en el período." />
            )}
          </O>
        </Panel>
        )}

        <Panel titulo="Reputación" subtitulo="Como la informa Mercado Libre">
          <O d={d} metrica="reputacion" onReintentar={onReintentar}>
            {r && r.nivel ? (
              <>
                {/* El termómetro: cinco tramos, el actual entero y los demás apagados. */}
                <div className="flex gap-1" aria-label={`Reputación ${NIVELES[r.nivel - 1]!.nombre}`}>
                  {NIVELES.map((n, i) => (
                    <div
                      key={n.nombre}
                      className="h-2.5 flex-1 rounded-full"
                      style={{ background: n.color, opacity: i + 1 === r.nivel ? 1 : 0.18 }}
                    />
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="font-medium text-brand-black">{NIVELES[r.nivel - 1]!.nombre}</span>
                  {r.medalla && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 py-0.5 px-2 text-xs font-medium text-amber-800">
                      <Medal className="h-3.5 w-3.5" /> MercadoLíder{' '}
                      {r.medalla === 'platinum' ? 'Platinum' : r.medalla === 'gold' ? 'Gold' : ''}
                    </span>
                  )}
                </div>
                {/* Las tasas solas: Mercado Libre no informa por API el tope de cada color, y
                    compararlas contra un número de memoria sería inventar. */}
                <ul className="mt-4 divide-y divide-neutral-100">
                  {[
                    { nombre: 'Reclamos', valor: r.reclamos },
                    { nombre: 'Despachos con demora', valor: r.demoras },
                    { nombre: 'Cancelaciones tuyas', valor: r.cancelaciones },
                  ].map((t) => (
                    <li key={t.nombre} className="flex items-baseline justify-between py-2 text-xs">
                      <span className="text-neutral-600">{t.nombre}</span>
                      <span className="font-medium tabular-nums text-brand-black">{tasa(t.valor)}</span>
                    </li>
                  ))}
                </ul>
                {r.ventasCompletadas !== null && (
                  <p className="mt-4 text-xs text-neutral-500">{numero(r.ventasCompletadas)} ventas completadas.</p>
                )}
              </>
            ) : (
              <SinDatos texto="La cuenta todavía no tiene reputación: Mercado Libre la calcula con las primeras ventas." />
            )}
          </O>
        </Panel>

        <Panel titulo="Atención al comprador" subtitulo="Lo que está esperando una respuesta tuya">
          <ul className="space-y-4">
            {[
              {
                metrica: 'preguntas' as const,
                icono: <MessageCircleQuestion className="h-5 w-5" />,
                valor: d.preguntasSinResponder,
                texto: 'preguntas sin responder',
              },
              {
                metrica: 'reclamos' as const,
                icono: <ShieldAlert className="h-5 w-5" />,
                valor: d.reclamosAbiertos,
                texto: 'reclamos abiertos',
              },
            ].map((x) => (
              <li key={x.texto}>
                <O d={d} metrica={x.metrica} onReintentar={onReintentar}>
                  <div className="flex items-center gap-4">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[rgb(var(--brand-primary-rgb)_/_0.07)] text-brand-primary">
                      {x.icono}
                    </span>
                    <span>
                      <span className="block text-2xl font-semibold text-brand-black">
                        {x.valor !== undefined ? numero(x.valor) : '—'}
                      </span>
                      <span className="block text-xs text-neutral-500">{x.texto}</span>
                    </span>
                  </div>
                </O>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs leading-relaxed text-neutral-500">Se responden desde Mercado Libre.</p>
        </Panel>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        {/* Visitas en su propio gráfico: en pesos y en visitas no comparten eje. */}
        <Panel titulo="Visitas por día" subtitulo="Visitantes únicos de tus publicaciones" className="lg:col-span-2">
          <O d={d} metrica="visitas" onReintentar={onReintentar}>
            {d.visitas && (
              <GraficoDeLineas
                fechas={fechas.slice(-d.visitas.serie.length)}
                formatear={numero}
                formatearEje={numero}
                alto={200}
                series={[{ id: 'visitas', nombre: 'Visitas', color: COLOR_DE_PLATAFORMA.mercadolibre, valores: d.visitas.serie }]}
              />
            )}
          </O>
        </Panel>
        {d.actual && (
        <Panel titulo="Visitas y ventas por publicación" subtitulo="Ordenadas de menor a mayor conversión">
          <O d={d} metrica="ventas" onReintentar={onReintentar}>
            {publicaciones.length > 0 ? (
              <>
                <table className="w-full text-left text-xs">
                  <thead className="text-neutral-500">
                    <tr>
                      <th className="pb-2 font-medium">Publicación</th>
                      <th className="pb-2 text-right font-medium">Visitas</th>
                      <th className="pb-2 text-right font-medium">Conv.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 tabular-nums">
                    {publicaciones
                      .map((p) => ({ ...p, conv: p.unidades / Math.max(1, p.visitas ?? 1) }))
                      .sort((x, y) => x.conv - y.conv)
                      .map((p) => (
                        <tr key={p.nombre}>
                          <td className="max-w-[140px] truncate py-2 pr-2 text-brand-black">{p.nombre}</td>
                          <td className="py-2 text-right text-neutral-600">{numero(p.visitas ?? 0)}</td>
                          <td className="py-2 text-right font-medium text-brand-black">{porcentaje(p.conv)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                <p className="mt-3 text-xs text-neutral-500">
                  Unidades vendidas cada 100 visitas, de lo que más vendiste en el período. Las de arriba son las que más
                  se miran sin venderse.
                </p>
              </>
            ) : (
              <SinDatos texto="No hubo ventas en el período." />
            )}
          </O>
        </Panel>
        )}
      </div>

      {/* Publicidad (Product Ads) no va: todavía no se lee, y un panel vacío sólo para decirlo es ruido. */}
      <div className="mt-3">
        <Panel titulo="Calidad de las publicaciones" subtitulo="El puntaje que les pone Mercado Libre, de 0 a 100">
          <O d={d} metrica="calidad" onReintentar={onReintentar}>
            {d.calidad && d.calidad.length > 0 ? (
              // Una barra por publicación: el largo es el puntaje, la pista es el resto hasta 100.
              // El nivel es el que informa Mercado Libre, no un corte nuestro.
              <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                {[...d.calidad]
                  .sort((x, y) => x.puntaje - y.puntaje)
                  .map((c) => (
                    <li key={c.publicacion}>
                      <div className="flex items-baseline justify-between gap-3 text-xs">
                        <span className="truncate text-brand-black">{c.publicacion}</span>
                        <span className="shrink-0 tabular-nums text-neutral-500">
                          <span className="font-medium text-brand-black">{c.puntaje}</span> · {c.nivel}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.07)]">
                        <div className="h-full rounded-full bg-brand-primary" style={{ width: `${c.puntaje}%` }} />
                      </div>
                    </li>
                  ))}
              </ul>
            ) : (
              <SinDatos texto="Mercado Libre todavía no puntuó tus publicaciones." />
            )}
          </O>
        </Panel>
      </div>
    </>
  );
}

// ── Tienda Nube ────────────────────────────────────────────────────────────

function DetalleDeTiendaNube({ d, onReintentar }: { d: MetricasDePlataforma; onReintentar: () => void }) {
  const c = d.carritosAbandonados;
  const e = d.envios;
  const clientes = d.clientes;
  return (
    <div className="mt-3 grid gap-3 lg:grid-cols-3">
      <Panel titulo="Carritos abandonados" subtitulo="Últimos 30 días (lo que guarda Tienda Nube)">
        <O d={d} metrica="carritos" onReintentar={onReintentar}>
          <div className="flex items-center gap-4">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[rgb(var(--brand-primary-rgb)_/_0.07)] text-brand-primary">
              <ShoppingCart className="h-6 w-6" />
            </span>
            <div>
              <p className="text-3xl font-semibold text-brand-black">{numero(c?.cantidad ?? 0)}</p>
              <p className="text-xs text-neutral-500">por {pesos(c?.monto ?? 0)} sin cerrar</p>
            </div>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-neutral-500">
            Checkouts que se empezaron y no se pagaron. Tienda Nube los guarda 30 días, así que este número no cambia
            con el período elegido.
          </p>
        </O>
      </Panel>

      <Panel titulo="Clientes" subtitulo="Quiénes compraron en el período">
        <O d={d} metrica="clientes" onReintentar={onReintentar}>
          {clientes && clientes.nuevos + clientes.recurrentes > 0 ? (
            <Reparto
              formatear={numero}
              partes={[
                { id: 'nuevos', nombre: 'Nuevos', valor: clientes.nuevos, color: COLOR_DE_PLATAFORMA.tiendanube },
                { id: 'recurrentes', nombre: 'Volvieron a comprar', valor: clientes.recurrentes, color: '#8A93A6' },
              ]}
            />
          ) : (
            <SinDatos texto="Nadie compró en el período." />
          )}
        </O>
      </Panel>

      <Panel titulo="Envíos" subtitulo="Órdenes pagas del período, según si ya se despacharon">
        <O d={d} metrica="envios" onReintentar={onReintentar}>
          <ul className="space-y-3">
            {[
              { icono: <PackageOpen className="h-4 w-4" />, nombre: 'Sin despachar', valor: e?.sinDespachar ?? 0, alerta: true },
              { icono: <PackageCheck className="h-4 w-4" />, nombre: 'Despachadas', valor: e?.despachadas ?? 0, alerta: false },
            ].map((x) => (
              <li key={x.nombre} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2 text-neutral-600">
                  <span className={x.alerta && x.valor > 0 ? 'text-amber-600' : 'text-neutral-400'}>{x.icono}</span>
                  {x.nombre}
                </span>
                <span className="font-medium tabular-nums text-brand-black">{numero(x.valor)}</span>
              </li>
            ))}
          </ul>
        </O>
      </Panel>

      <Panel titulo="Stock de lo más vendido" subtitulo="Por variante, con lo que vendió en el período" className="lg:col-span-2">
        <O d={d} metrica="stock" onReintentar={onReintentar}>
          {d.stock && d.stock.length > 0 ? (
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-neutral-500">
                <tr>
                  <th className="pb-2 font-medium">Producto</th>
                  <th className="pb-2 font-medium">Variante</th>
                  <th className="pb-2 text-right font-medium">Vendidas</th>
                  <th className="pb-2 text-right font-medium">Stock hoy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 tabular-nums">
                {d.stock.map((s) => (
                  <tr key={`${s.producto}-${s.variante}`}>
                    <td className="max-w-[180px] truncate py-2 pr-2 text-brand-black">{s.producto}</td>
                    <td className="py-2 pr-2 text-neutral-600">{s.variante ?? '—'}</td>
                    <td className="py-2 text-right text-brand-black">{numero(s.vendidas)}</td>
                    {/* Sin stock va en rojo: es un dato (cero), no un umbral nuestro. */}
                    <td className={`py-2 text-right font-medium ${s.stock === 0 ? 'text-red-600' : 'text-brand-black'}`}>
                      {s.stock === null ? (
                        <span className="font-normal text-neutral-400">Sin control</span>
                      ) : s.stock === 0 ? (
                        'Agotado'
                      ) : (
                        numero(s.stock)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <SinDatos texto="No hubo ventas en el período." />
          )}
        </O>
      </Panel>

      <Panel titulo="Margen estimado" subtitulo="Sólo de los productos con costo cargado en Tienda Nube">
        <O d={d} metrica="margen" onReintentar={onReintentar}>
          {d.margen && d.margen.ventasConCosto > 0 ? (
            <>
              <p className="text-3xl font-semibold text-brand-black">
                {porcentaje((d.margen.ventasConCosto - d.margen.costoDeLoVendido) / d.margen.ventasConCosto)}
              </p>
              <p className="mt-1 text-xs text-neutral-500">
                {pesosCortos(d.margen.ventasConCosto - d.margen.costoDeLoVendido)} de ganancia sobre{' '}
                {pesosCortos(d.margen.ventasConCosto)} vendidos
              </p>
              <p className="mt-4 rounded-xl bg-neutral-50 p-3 text-xs leading-relaxed text-neutral-600">
                Calculado con {d.margen.productosConCosto} de los {d.margen.productosVendidos} productos que vendiste: los
                demás no tienen costo cargado. Se carga en cada producto, desde el panel de Tienda Nube.
              </p>
            </>
          ) : (
            <SinDatos texto="Ningún producto vendido tiene costo cargado en Tienda Nube. Cuando lo cargues, aparece el margen." />
          )}
        </O>
      </Panel>

      <Panel titulo="Cupones usados" subtitulo="Cuántas veces y cuánto descontaron" className="lg:col-span-3">
        <O d={d} metrica="ventas" onReintentar={onReintentar}>
          {d.cupones && d.cupones.length > 0 ? (
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-neutral-500">
                <tr>
                  <th className="pb-2 font-medium">Cupón</th>
                  <th className="pb-2 text-right font-medium">Usos</th>
                  <th className="pb-2 text-right font-medium">Descuento total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 tabular-nums">
                {d.cupones.map((cu) => (
                  <tr key={cu.codigo}>
                    <td className="py-2.5">
                      <span className="rounded-md bg-neutral-100 py-0.5 px-2 font-mono text-xs text-brand-black">{cu.codigo}</span>
                    </td>
                    <td className="py-2.5 text-right text-brand-black">{numero(cu.usos)}</td>
                    <td className="py-2.5 text-right text-brand-black">{pesos(cu.descuento)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <SinDatos texto="No se usaron cupones en el período." />
          )}
        </O>
      </Panel>
    </div>
  );
}
